import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Reveal, SectionTag } from "../components/bits";

const FAQS = [
  {
    q: "Are your wattage claims real?",
    a: "Yes. We quote real power draw at the wall, not inflated 'HID equivalent' numbers. Every product page lists true LED watts, diode counts and actual coverage footprints. Read our True Watts guide for the full story on industry wattage inflation.",
    link: { to: "/guides/true-watts-of-led-grow-lights", label: "Read the True Watts guide" },
  },
  {
    q: "How long does shipping take?",
    a: "Every order ships free via express post anywhere in Australia. Orders are dispatched from our Australian warehouse within 1–2 business days, and most arrive within 2–5 business days with tracking emailed to you.",
  },
  {
    q: "What warranty do your lights carry?",
    a: "Every SuperTechLED fixture is covered by a 2-year Australian warranty on diodes, drivers and build quality, handled locally — no overseas RMA runaround. See our Shipping & Warranty page for the details.",
    link: { to: "/shipping", label: "Shipping & warranty details" },
  },
  {
    q: "Can I return a light if it's not right?",
    a: "Yes — we offer 30-day hassle-free returns. If the light doesn't suit your space, email us within 30 days of delivery and we'll arrange the return and refund.",
  },
  {
    q: "How high should I hang my LED grow light?",
    a: "It depends on the fixture and growth stage — as a rule of thumb, 30–60 cm from the canopy. Our hanging distance guide lists exact veg and bloom distances for every model we sell.",
    link: { to: "/guides/led-hanging-distance-guide", label: "Hanging distance guide" },
  },
  {
    q: "Which light do I need for my tent?",
    a: "Match the fixture's coverage footprint to your canopy area, not the tent size alone. As a rough guide: 60×60 cm tents suit our compact COB and small quantum boards, 120×120 cm suits the mid-range Quantum Sun and Galaxy models, and larger rooms suit the StarShip or CommandShip bars. Email us your setup and we'll recommend one.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards through our secure Stripe checkout, plus PayPal. All prices are in Australian dollars.",
  },
  {
    q: "Do you ship outside Australia?",
    a: "We currently ship within Australia only, which lets us keep shipping free and fast and honour the local warranty properly.",
  },
];

export default function Faq() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="mx-auto max-w-3xl px-5 pb-28 pt-28 sm:px-8" data-testid="faq-page">
      <SEO
        title="FAQ — LED Grow Light Questions Answered | SuperTechLED"
        description="Answers to common SuperTechLED questions: real wattage, free express shipping times, 2-year warranty, 30-day returns, hanging distances and choosing the right light for your tent."
        jsonLd={jsonLd}
      />
      <Reveal>
        <SectionTag>Good to know</SectionTag>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Frequently asked questions
        </h1>
        <p className="mt-4 max-w-xl text-sm text-[#9FB8A8] sm:text-base">
          Straight answers about our lights, shipping, warranty and growing under LED.
        </p>
      </Reveal>

      <div className="mt-12 space-y-4">
        {FAQS.map((f, i) => (
          <Reveal key={f.q} delay={Math.min(i * 0.05, 0.3)}>
            <div className="rounded-2xl border border-[#39FF14]/15 bg-[#071009] p-6 sm:p-7" data-testid={`faq-item-${i}`}>
              <h2 className="font-display text-base font-bold text-[#F0FDF4] sm:text-lg">{f.q}</h2>
              <p className="mt-2.5 text-sm leading-relaxed text-[#9FB8A8]">{f.a}</p>
              {f.link && (
                <Link to={f.link.to} className="mt-3 inline-block font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#39FF14] hover:text-[#86EFAC]">
                  {f.link.label}
                </Link>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12 text-center">
        <p className="text-sm text-[#9FB8A8]">
          Still stuck? <a href="mailto:supertechled@outlook.com" className="text-[#39FF14] hover:text-[#86EFAC]">Email us</a> — we reply within one business day.
        </p>
      </Reveal>
    </div>
  );
}
